import type {CalypsoCatalog} from "#pricing/catalogs/calypso/catalog"
import {createAmountTriggeredPriceNode, createPriceContainer, createVariantConditionedPriceNodes} from "#pricing/catalogs/graph-helpers"
import type {ConfigurationGroupNode} from "#pricing/nodes/configuration-group-node"
import {MeshAmountNode} from "#pricing/nodes/mesh-amount-node"
import type {MeshesNode} from "#pricing/nodes/meshes-node"
import type {PriceContainer} from "#pricing/nodes/price-container"
import {VariantConditionNode} from "#pricing/nodes/variant-condition-node"

export function createLesinaBasinContainer(catalog: CalypsoCatalog, Meshes: MeshesNode): PriceContainer {
    const lesina910Amount = new MeshAmountNode({parameters: {name: "9211 - Lesina Glass Vanity Basin 910 White", meshes: Meshes}})
    const lesina910 = createAmountTriggeredPriceNode(catalog, "9211", lesina910Amount)

    const lesina1220Amount = new MeshAmountNode({parameters: {name: "9212 - Lesina Glass Vanity Basin 1220 White", meshes: Meshes}})
    const lesina1220 = createAmountTriggeredPriceNode(catalog, "9212", lesina1220Amount)

    return createPriceContainer("Lesina Basin", "Lesina Basin", [lesina910, lesina1220], true)
}

export function createTressaBasinContainer(catalog: CalypsoCatalog, Meshes: MeshesNode): PriceContainer {
    const tressa910Amount = new MeshAmountNode({parameters: {name: "9222 - Tressa Cast Vanity Basin 910 White", meshes: Meshes}})
    const tressa910 = createAmountTriggeredPriceNode(catalog, "9222", tressa910Amount)

    const tressa1220Amount = new MeshAmountNode({parameters: {name: "9223 - Tressa Cast Vanity Basin 1220 White", meshes: Meshes}})
    const tressa1220 = createAmountTriggeredPriceNode(catalog, "9223", tressa1220Amount)

    return createPriceContainer("Tressa Basin", "Tressa Basin", [tressa910, tressa1220], true)
}

/**
 * The Allerona basins come in one white finish only, so mesh presence alone prices them. Each size exists as a tap-holed
 * and a no-tap-hole SKU sharing one description, hence the SKU-based lookups.
 */
export function createAlleronaBasinContainer(catalog: CalypsoCatalog, Meshes: MeshesNode): PriceContainer {
    const allerona600Amount = new MeshAmountNode({parameters: {name: "3564-Allerona Cast Vanity Basin 600 1TH White", meshes: Meshes}})
    const allerona600 = createAmountTriggeredPriceNode(catalog, "3564", allerona600Amount)

    const allerona600NthAmount = new MeshAmountNode({parameters: {name: "3697 - Allerona Cast Vanity Basin 600mm NTH", meshes: Meshes}})
    const allerona600Nth = createAmountTriggeredPriceNode(catalog, "3697", allerona600NthAmount)

    const allerona800Amount = new MeshAmountNode({parameters: {name: "3565-Allerona Cast Vanity Basin 800 1TH White", meshes: Meshes}})
    const allerona800 = createAmountTriggeredPriceNode(catalog, "3565", allerona800Amount)

    const allerona800NthAmount = new MeshAmountNode({parameters: {name: "3698- Allerona Cast Vanity Basin 800mm NTH", meshes: Meshes}})
    const allerona800Nth = createAmountTriggeredPriceNode(catalog, "3698", allerona800NthAmount)

    const allerona1000Amount = new MeshAmountNode({parameters: {name: "3566-Allerona Cast Vanity Basin 1000 1TH White", meshes: Meshes}})
    const allerona1000 = createAmountTriggeredPriceNode(catalog, "3566", allerona1000Amount)

    const allerona1000NthAmount = new MeshAmountNode({parameters: {name: "3699- Allerona Cast Vanity Basin 1000mm NTH", meshes: Meshes}})
    const allerona1000Nth = createAmountTriggeredPriceNode(catalog, "3699", allerona1000NthAmount)

    const allerona1200Amount = new MeshAmountNode({parameters: {name: "3567-Allerona Cast Vanity Basin 1200 2TH White", meshes: Meshes}})
    const allerona1200 = createAmountTriggeredPriceNode(catalog, "3567", allerona1200Amount)

    const allerona1200NthAmount = new MeshAmountNode({parameters: {name: "3700 - Allerona Cast Vanity Basin 1200mm NTH", meshes: Meshes}})
    const allerona1200Nth = createAmountTriggeredPriceNode(catalog, "3700", allerona1200NthAmount)

    return createPriceContainer(
        "Allerona Basin",
        "Allerona Basin",
        [allerona600, allerona600Nth, allerona800, allerona800Nth, allerona1000, allerona1000Nth, allerona1200, allerona1200Nth],
        true,
    )
}

/**
 * Mesh presence triggers the bowl, the ceramic finish comes from the basin finish variant. The BasinFinish variant ids
 * equal the product SKUs, so the finish dependency is taken from the node's uniqueId; a description-keyed config map
 * can't be used here because the finish texts collide on includes() matching ("White" ⊂ "Matt White").
 */
export function createToledoBasinContainer(catalog: CalypsoCatalog, Meshes: MeshesNode, BasinFinish: ConfigurationGroupNode): PriceContainer {
    const toledoAmount = new MeshAmountNode({parameters: {name: "9282 - Toledo Ceramic Sit-On Bowl Dia 380mm", meshes: Meshes}})
    const toledoUnits = createVariantConditionedPriceNodes(catalog, "Toledo", 3, undefined, toledoAmount)
    toledoUnits.forEach((node) => node.addDependency(BasinFinish, node.getUniqueId(), ""))

    return createPriceContainer("Toledo Basin", "Toledo Basin", toledoUnits, true)
}

/**
 * Same setup as the Toledo bowl, in two finishes. Templates whose basin finish variant ids are not the SKUs pass a description-keyed
 * finish map instead of the group ("White" is not contained in "Matt Black", so the keys don't collide).
 */
export function createIndianaBasinContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    basinFinish: ConfigurationGroupNode | Map<string, [ConfigurationGroupNode, string]>,
): PriceContainer {
    const indianaAmount = new MeshAmountNode({parameters: {name: "9317 - Indiana Ceramic Sit-On Bowl 360 dia x 120 Fluted", meshes: Meshes}})
    const finishConfigMap = basinFinish instanceof Map ? basinFinish : undefined
    const indianaUnits = createVariantConditionedPriceNodes(catalog, "Indiana", 2, finishConfigMap, indianaAmount)
    if (!(basinFinish instanceof Map)) indianaUnits.forEach((node) => node.addDependency(basinFinish, node.getUniqueId(), ""))

    return createPriceContainer("Indiana Basin", "Indiana Basin", indianaUnits, true)
}

/** The Dakota basin comes in one finish only, so mesh presence alone prices it. */
export function createDakotaBasinContainer(catalog: CalypsoCatalog, Meshes: MeshesNode): PriceContainer {
    const dakotaAmount = new MeshAmountNode({parameters: {name: "9294 - Dakota Ceramic Sit-on Basin 500 x 360 x 140mm Oval", meshes: Meshes}})
    const dakota = createAmountTriggeredPriceNode(catalog, "9294", dakotaAmount)

    return createPriceContainer("Dakota Basin", "Dakota Basin", [dakota], true)
}

/** Both Bouro sizes come in gloss white only, so mesh presence alone prices them. */
export function createBouroBasinContainer(catalog: CalypsoCatalog, Meshes: MeshesNode): PriceContainer {
    const bouro470Amount = new MeshAmountNode({parameters: {name: "3584 - Bouro Sit-On Cast Basin - 470 470 x 360 x 160mm", meshes: Meshes}})
    const bouro470 = createAmountTriggeredPriceNode(catalog, "3584", bouro470Amount)

    const bouro600Amount = new MeshAmountNode({parameters: {name: "3585 - Bouro Sit-On Cast Basin - 600 600 x 360 x 160mm", meshes: Meshes}})
    const bouro600 = createAmountTriggeredPriceNode(catalog, "3585", bouro600Amount)

    return createPriceContainer("Bouro Basin", "Bouro Basin", [bouro470, bouro600], true)
}

/** The Bellanti basin comes in one finish only, so mesh presence alone prices it. */
export function createBellantiBasinContainer(catalog: CalypsoCatalog, Meshes: MeshesNode): PriceContainer {
    const bellantiAmount = new MeshAmountNode({parameters: {name: "9069 - Bellanti Sit-On Cast Basin 460 x 320mm NTH", meshes: Meshes}})
    const bellanti = createAmountTriggeredPriceNode(catalog, "9069", bellantiAmount)

    return createPriceContainer("Bellanti Basin", "Bellanti Basin", [bellanti], true)
}

/** The Florida basin comes in one finish only, so mesh presence alone prices it. */
export function createFloridaBasinContainer(catalog: CalypsoCatalog, Meshes: MeshesNode): PriceContainer {
    const floridaAmount = new MeshAmountNode({parameters: {name: "9199 - Florida Ceramic Sit-on Basin 380 x 380 x 135mm Round", meshes: Meshes}})
    const florida = createAmountTriggeredPriceNode(catalog, "9199", floridaAmount)

    return createPriceContainer("Florida Basin", "Florida Basin", [florida], true)
}

/** The tap type variants that decide between the one tap hole (1TH, deck mounted taps) and no tap hole (NTH, wall mounted taps) SKUs. */
export type TapHoleVariants = {group: ConfigurationGroupNode; oneTapHole: string; noTapHole: string}

/** Creates one node per SKU (search term = exact SKU), conditioned on the tap hole variant and, if given, the finish. */
function createTapHoleConditionedPriceNodes(
    catalog: CalypsoCatalog,
    skus: string[],
    tapHoleGroup: ConfigurationGroupNode,
    tapHoleVariant: string,
    finishConfigMap: Map<string, [ConfigurationGroupNode, string]> | undefined,
    amount: MeshAmountNode,
) {
    return skus.flatMap((sku) => {
        const nodes = createVariantConditionedPriceNodes(catalog, sku, 1, finishConfigMap, amount)
        nodes.forEach((node) => node.addDependency(tapHoleGroup, tapHoleVariant, ""))
        return nodes
    })
}

/**
 * Idro basins: mesh presence triggers the bowl, the basin finish variant (keys "Gloss White" / "Matt White") and the tap type the catalog
 * row. The template uses the one tap hole mesh for both tap types. The catalog lists every size as one tap hole (92xx) and no tap hole
 * (93xx) SKUs with identical descriptions (the distinguishing desc3 column is not part of the description), so exact SKUs are used.
 */
export function createIdroBasinContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    basinFinishConfigMap: Map<string, [ConfigurationGroupNode, string]>,
    tapHole: TapHoleVariants,
): PriceContainer {
    const {group, oneTapHole, noTapHole} = tapHole

    const idro600Amount = new MeshAmountNode({parameters: {name: "9232 - Idro Cast Basin 600mm 1TH", meshes: Meshes}})
    const idro600Units = [
        ...createTapHoleConditionedPriceNodes(catalog, ["9232", "9233"], group, oneTapHole, basinFinishConfigMap, idro600Amount),
        ...createTapHoleConditionedPriceNodes(catalog, ["9332", "9333"], group, noTapHole, basinFinishConfigMap, idro600Amount),
    ]

    //No 800mm Idro: the centre bowl (9263) has no no tap hole version and the left bowl basins are phased out, so the customer removed
    //the Idro from the 800 vanity.
    const idro1200Amount = new MeshAmountNode({parameters: {name: "9238 - Idro Cast Basin 1200mm Double Bowl - 2x 1TH", meshes: Meshes}})
    const idro1200Units = [
        ...createTapHoleConditionedPriceNodes(catalog, ["9238", "9239"], group, oneTapHole, basinFinishConfigMap, idro1200Amount),
        ...createTapHoleConditionedPriceNodes(catalog, ["9338", "9339"], group, noTapHole, basinFinishConfigMap, idro1200Amount),
    ]

    return createPriceContainer("Idro Basin", "Idro Basin", [...idro600Units, ...idro1200Units], true)
}

/** Orani basins: same setup and same 1TH/NTH SKU duplication as the Idro basins. */
export function createOraniBasinContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    basinFinishConfigMap: Map<string, [ConfigurationGroupNode, string]>,
    tapHole: TapHoleVariants,
): PriceContainer {
    const {group, oneTapHole, noTapHole} = tapHole

    const orani600Amount = new MeshAmountNode({parameters: {name: "9240-Orani Cast Vanity Basin 600 1TH Gloss White", meshes: Meshes}})
    const orani600Units = [
        ...createTapHoleConditionedPriceNodes(catalog, ["9240", "9241"], group, oneTapHole, basinFinishConfigMap, orani600Amount),
        ...createTapHoleConditionedPriceNodes(catalog, ["9340", "9341"], group, noTapHole, basinFinishConfigMap, orani600Amount),
    ]

    const orani800Amount = new MeshAmountNode({parameters: {name: "9242-Orani Cast Vanity Basin 800 1TH Gloss White", meshes: Meshes}})
    const orani800Units = [
        ...createTapHoleConditionedPriceNodes(catalog, ["9242", "9243"], group, oneTapHole, basinFinishConfigMap, orani800Amount),
        ...createTapHoleConditionedPriceNodes(catalog, ["9342", "9343"], group, noTapHole, basinFinishConfigMap, orani800Amount),
    ]

    //The 1200mm Orani exists in gloss white only (the matt one was discontinued and removed from the configurator), so it has no basin
    //finish dependency.
    const orani1200Amount = new MeshAmountNode({parameters: {name: "9244 - Orani Cast Basin 1200mm Double Bowl - 2x 1TH", meshes: Meshes}})
    const orani1200Units = [
        ...createTapHoleConditionedPriceNodes(catalog, ["9244"], group, oneTapHole, undefined, orani1200Amount),
        ...createTapHoleConditionedPriceNodes(catalog, ["9344"], group, noTapHole, undefined, orani1200Amount),
    ]

    return createPriceContainer("Orani Basin", "Orani Basin", [...orani600Units, ...orani800Units, ...orani1200Units], true)
}

/**
 * Both Serrenti sizes come in white only, so mesh presence and the tap type price them. The template uses the no tap hole mesh for both
 * tap types. The catalog marks 9354/9355 as no tap hole; 9200/9201 carry no tap hole information but are the one tap hole versions
 * (confirmed by the customer).
 */
export function createSerrentiBasinContainer(catalog: CalypsoCatalog, Meshes: MeshesNode, tapHole: TapHoleVariants): PriceContainer {
    const {group, oneTapHole, noTapHole} = tapHole

    const serrenti610Amount = new MeshAmountNode({parameters: {name: "9354 - Serrenti Ceramic Vanity Basin 610 x 460 x 15mm NTH", meshes: Meshes}})
    const serrenti610Units = [
        ...createTapHoleConditionedPriceNodes(catalog, ["9200"], group, oneTapHole, undefined, serrenti610Amount),
        ...createTapHoleConditionedPriceNodes(catalog, ["9354"], group, noTapHole, undefined, serrenti610Amount),
    ]

    const serrenti810Amount = new MeshAmountNode({parameters: {name: "9355 - Serrenti Ceramic Vanity Basin 810 x 460 x 15mm NTH", meshes: Meshes}})
    const serrenti810Units = [
        ...createTapHoleConditionedPriceNodes(catalog, ["9201"], group, oneTapHole, undefined, serrenti810Amount),
        ...createTapHoleConditionedPriceNodes(catalog, ["9355"], group, noTapHole, undefined, serrenti810Amount),
    ]

    return createPriceContainer("Serrenti Basin", "Serrenti Basin", [...serrenti610Units, ...serrenti810Units], true)
}

/**
 * Orta glass basins: mesh presence triggers the bowl, the basin colour variant the catalog row. White and black have their own SKUs with
 * the same description apart from the colour, and the bowl position (left/right) is only in the desc3 column that the parser drops, so
 * exact SKUs are used.
 */
export function createOrtaBasinContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    colour: {group: ConfigurationGroupNode; white: string; black: string},
): PriceContainer {
    const createOrtaNodes = (meshName: string, whiteSku: string, blackSku: string) => {
        const amount = new MeshAmountNode({parameters: {name: meshName, meshes: Meshes}})
        return [
            [whiteSku, colour.white],
            [blackSku, colour.black],
        ].flatMap(([sku, variantId]) => {
            const nodes = createVariantConditionedPriceNodes(catalog, sku, 1, undefined, amount)
            nodes.forEach((node) => node.addDependency(colour.group, variantId, ""))
            return nodes
        })
    }

    const orta700Units = createOrtaNodes("9215 - Orta Glass Basin 700mm", "9215", "9268")
    const orta1040RightUnits = createOrtaNodes("9216 - Orta Winged Glass Basin 1040mm Right Bowl", "9216", "9269")
    const orta1040LeftUnits = createOrtaNodes("9217 - Orta Winged Glass Basin 1040mm Left Bowl", "9217", "9270")
    const orta1380Units = createOrtaNodes("9218 - Orta Winged Glass Basin 1380mm Centre Bowl", "9218", "9271")

    return createPriceContainer("Orta Basin", "Orta Basin", [...orta700Units, ...orta1040RightUnits, ...orta1040LeftUnits, ...orta1380Units], true)
}

/**
 * Farra cast basins: mesh presence triggers the bowl, the selected tap the tap hole. The template uses the same mesh for all taps. The
 * deck mounted taps need the one tap hole SKUs (92xx), the wall mounted taps the no tap hole SKUs (93xx, "NTH"). Several variants select
 * the one tap hole row, so its condition is an OR over them.
 */
export function createFarraBasinContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    tap: {group: ConfigurationGroupNode; oneTapHole: string[]; noTapHole: string[]},
): PriceContainer {
    const createFarraNodes = (meshName: string, oneTapHoleSku: string, noTapHoleSku: string) => {
        const amount = new MeshAmountNode({parameters: {name: meshName, meshes: Meshes}})
        return [
            [oneTapHoleSku, tap.oneTapHole],
            [noTapHoleSku, tap.noTapHole],
        ].flatMap(([sku, variantIds]) => {
            const nodes = createVariantConditionedPriceNodes(catalog, sku as string, 1, undefined, amount)
            nodes.forEach((node) => {
                const condition = node.parameters.condition
                if (!(condition instanceof VariantConditionNode)) throw new Error("Farra basin node without variant condition")
                condition.parameters.condition.variantOperator = "OR"
                ;(variantIds as string[]).forEach((variantId) => node.addDependency(tap.group, variantId, ""))
            })
            return nodes
        })
    }

    const farra695Units = createFarraNodes("9264 - Farra Cast Basin 695mm", "9264", "9361")
    const farra1035RightUnits = createFarraNodes("9265 - Farra Cast Wing Basin 1035mm Right Bowl", "9265", "9362")
    const farra1035LeftUnits = createFarraNodes("9266 - Farra Cast Wing Basin 1035mm Left Bowl", "9266", "9363")
    const farra1375Units = createFarraNodes("9267 - Farra Cast Wing Basin 1375mm Centre Bowl", "9267", "9364")

    return createPriceContainer("Farra Basin", "Farra Basin", [...farra695Units, ...farra1035RightUnits, ...farra1035LeftUnits, ...farra1375Units], true)
}

/** The Vinci basin comes in matt white only, so mesh presence alone prices it. */
export function createVinciBasinContainer(catalog: CalypsoCatalog, Meshes: MeshesNode): PriceContainer {
    const vinciAmount = new MeshAmountNode({parameters: {name: "3689 - Vinci Sit-On Cast Basin Dia 400 x 400 x 170mm", meshes: Meshes}})
    const vinci = createAmountTriggeredPriceNode(catalog, "3689", vinciAmount)

    return createPriceContainer("Vinci Basin", "Vinci Basin", [vinci], true)
}

/** Both Forza sizes come in white only, so mesh presence alone prices them. The mesh names use the nominal 700/900 size, the catalog descriptions the actual 710/910 width. */
export function createForzaBasinContainer(catalog: CalypsoCatalog, Meshes: MeshesNode): PriceContainer {
    const forza700Amount = new MeshAmountNode({parameters: {name: "9277 - Forza 700", meshes: Meshes}})
    const forza700 = createAmountTriggeredPriceNode(catalog, ["Forza Cast Vanity Basin", "710"], forza700Amount)

    const forza900Amount = new MeshAmountNode({parameters: {name: "9278 - Forza 900", meshes: Meshes}})
    const forza900 = createAmountTriggeredPriceNode(catalog, ["Forza Cast Vanity Basin", "910"], forza900Amount)

    return createPriceContainer("Forza Basin", "Forza Basin", [forza700, forza900], true)
}
